import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import PromoStrip from "@/components/PromoStrip";
import CategoryTabs from "@/components/CategoryTabs";
import ProductCard from "@/components/ProductCard";
import CartDrawer from "@/components/CartDrawer";
import { API, BACKEND_URL, formatARS } from "@/lib/format";
import { withParams } from "@/lib/attribution";
import { useCart } from "@/context/CartContext";
import { Loader2, Pizza, ChevronRight, ArrowLeft, Sparkles, X, Tag } from "lucide-react";

const slugify = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const CAT_URL = { piedra: "/pizzas/piedra", molde: "/pizzas/molde", xl: "/pizzas/xl", empanadas: "/empanadas", bebidas: "/bebidas", dulces: "/dulces" };

// path -> [lockedCategory, category]
const PATH_SECTION = {
  "/pizzas/piedra": ["piedra", "piedra"],
  "/pizzas/molde": ["molde", "molde"],
  "/pizzas/xl": [null, "xl"],
  "/empanadas": ["empanadas", "empanadas"],
  "/bebidas": [null, "bebidas"],
  "/dulces": [null, "dulces"],
};

const initialFromPath = (p) => {
  if (p === "/promos") return { intro: 0, lock: null, cat: "todos" };
  if (p === "/pizzas") return { intro: 2, lock: null, cat: "todos" };
  const entry = PATH_SECTION[p];
  if (entry) return { intro: 0, lock: entry[0], cat: entry[1] };
  return { intro: 1, lock: null, cat: "todos" };
};

const DEFAULT_INTRO = {
  paso1Titulo: "¿Qué querés comer hoy?",
  paso2Titulo: "¿A la piedra o al molde?",
  pizzas: { titulo: "Pizzas", subtitulo: "Piedra · Molde", imagen: null },
  empanadas: { titulo: "Empanadas", subtitulo: "12 sabores · docena promo", imagen: null },
  piedra: { titulo: "A la Piedra", subtitulo: "Masa fina y crocante", imagen: null },
  molde: { titulo: "Al Molde", subtitulo: "Masa esponjosa y alta", imagen: null },
};

const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const LOCKED_LABELS = {
  piedra: "Pizzas a la Piedra",
  molde: "Pizzas al Molde",
  empanadas: "Empanadas",
  bebidas: "Bebidas",
  dulces: "Dulces",
};

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(() => initialFromPath(window.location.pathname).cat);
  const [introStep, setIntroStep] = useState(() => initialFromPath(window.location.pathname).intro); // 0 = cerrada, 1 = pizza/empanada, 2 = piedra/molde
  const [lockedCategory, setLockedCategory] = useState(() => initialFromPath(window.location.pathname).lock);
  const [cfg, setCfg] = useState(null);
  useEffect(() => { axios.get(`${API}/config`).then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const [intro, setIntro] = useState(DEFAULT_INTRO);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [promoRules, setPromoRules] = useState([]);
  const menuRef = useRef(null);
  const { promoMode, clearPromoMode, setPromoMode, items: cartItems, setIsOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const promoSlug = path.startsWith("/promo/") ? path.slice("/promo/".length) : null;
  const [vistaPromos, setVistaPromos] = useState(path === "/promos");

  const pmap = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const matchesTarget = (t, cartItem) => {
    if (t.categoria) {
      const prod = pmap.get(cartItem.productoId);
      if (!prod || prod.categoria !== t.categoria) return false;
    } else if (t.productoId && cartItem.productoId !== t.productoId) {
      return false;
    }
    if (t.sabores?.length && !t.sabores.includes(cartItem.productoId)) return false;
    if (t.tamano && (cartItem.tamano || null) !== t.tamano) return false;
    return true;
  };

  const promoProgress = useMemo(() => {
    if (!promoMode) return null;
    const targets = promoMode.targets.map((t) => {
      const inCart = cartItems.filter((i) => matchesTarget(t, i)).reduce((s, i) => s + i.cantidad, 0);
      return { ...t, remaining: Math.max(0, t.cantidad - inCart) };
    });
    return { targets, done: targets.every((t) => t.remaining === 0) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoMode, cartItems, pmap]);

  useEffect(() => {
    if (promoMode && promoProgress?.done) {
      toast.success(`¡${promoMode.nombre} completa!`, { description: "La promo ya se aplicó a tu pedido" });
      const slug = promoMode.slug;
      clearPromoMode();
      setIsOpen(true);
      if (slug) navigate(withParams("/"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoProgress?.done]);

  // URLs por sección: /pizzas, /pizzas/piedra, /pizzas/molde, /empanadas, /promos, /promo/:slug
  // La pantalla SIEMPRE sigue a la URL (incluye atrás del navegador/celular).
  useEffect(() => {
    if (promoSlug) return; // lo maneja el efecto de promo
    setVistaPromos(path === "/promos");
    if (path === "/") {
      setIntroStep(1);
      setLockedCategory(null);
      setCategory("todos");
    } else if (path === "/promos") {
      setIntroStep(0); setLockedCategory(null); setCategory("todos");
    } else if (path === "/pizzas") {
      // /pizzas abre la pantalla "¿A la piedra o al molde?", no el menú completo
      setIntroStep(2); setLockedCategory(null); setCategory("todos");
    } else if (PATH_SECTION[path]) {
      const [lock, cat] = PATH_SECTION[path];
      setIntroStep(0); setLockedCategory(lock); setCategory(cat);
      window.scrollTo({ top: 0, behavior: "instant" });
    }
    // salir de /promo/* con atrás cancela el armado
    clearPromoMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, promoSlug]);

  // Entrada directa a una promo por URL: abre el armado de esa promo
  useEffect(() => {
    if (!promoSlug || promoRules.length === 0) return;
    const rule = promoRules.find((r) => slugify(r.nombre) === promoSlug);
    if (!rule) {
      toast.error("Esa promo no está disponible hoy");
      navigate(withParams("/"));
      return;
    }
    if (promoMode?.promoId === rule.id) return;
    setIntroStep(0);
    setVistaPromos(false);
    setLockedCategory(null);
    setPromoMode({
      promoId: rule.id,
      nombre: rule.nombre,
      slug: promoSlug,
      targets: rule.items.map((req) => ({
        categoria: req.categoria || null,
        productoId: req.productoId || null,
        sabores: req.sabores || [],
        tamano: req.tamano || null,
        cantidad: req.cantidad,
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoSlug, promoRules]);

  const promoLockFor = (p) => {
    if (!promoProgress) return null;
    const matches = promoProgress.targets.filter((t) =>
      t.categoria
        ? p.categoria === t.categoria && (!t.sabores?.length || t.sabores.includes(p.id))
        : p.id === t.productoId,
    );
    if (matches.length === 0) return { allowed: false };
    const remaining = matches.reduce((s, t) => s + t.remaining, 0);
    // Lo que ya está en el carrito queda habilitado para poder cambiarlo
    const enCarrito = cartItems.some((i) => i.productoId === p.id);
    return { allowed: remaining > 0 || enCarrito, remaining };
  };

  const CAT_LABEL = { empanadas: "empanada", piedra: "pizza", molde: "pizza", xl: "pizza XL", bebidas: "bebida", dulces: "dulce" };
  const TAM_PLURAL = { grande: "grandes", chica: "chicas" };
  const faltantesTexto = (targets) =>
    targets
      .filter((t) => t.remaining > 0)
      .map((t) => {
        const cat = t.categoria || pmap.get(t.productoId)?.categoria;
        const base = CAT_LABEL[cat] || "producto";
        const noun = t.remaining > 1 && !base.endsWith("XL") ? `${base}s` : base;
        const tam = t.tamano ? ` ${t.remaining > 1 ? TAM_PLURAL[t.tamano] || t.tamano : t.tamano}` : "";
        return `${t.remaining} ${noun}${tam}`;
      })
      .join(" · ");

  useEffect(() => {
    let cancelado = false;
    const cargarMenu = (intento = 0) => {
      axios.get(`${API}/menu`, { timeout: 20000 })
        .then((r) => { if (!cancelado) setProducts(r.data); })
        .catch(() => {
          if (!cancelado && intento < 2) setTimeout(() => cargarMenu(intento + 1), 1200);
          else if (!cancelado) setProducts([]);
        })
        .finally(() => { if (!cancelado) setLoading(false); });
    };
    cargarMenu();
    axios.get(`${API}/config`).then((r) => {
      const i = r.data?.intro || {};
      setIntro({
        ...DEFAULT_INTRO, ...i,
        pizzas: { ...DEFAULT_INTRO.pizzas, ...(i.pizzas || {}) },
        empanadas: { ...DEFAULT_INTRO.empanadas, ...(i.empanadas || {}) },
        piedra: { ...DEFAULT_INTRO.piedra, ...(i.piedra || {}) },
        molde: { ...DEFAULT_INTRO.molde, ...(i.molde || {}) },
      });
    }).catch(() => {}).finally(() => setConfigLoaded(true));
    axios.get(`${API}/promos/activas`).then((r) => setPromoRules(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setConfigLoaded(true), 2500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    document.body.style.overflow = introStep > 0 ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [introStep]);

  const combos = useMemo(() => products.filter((p) => p.esCombo), [products]);

  const activeCategory = lockedCategory || category;

  const aplicaAPromo = (p) =>
    promoProgress?.targets.some((t) =>
      t.categoria
        ? p.categoria === t.categoria && (!t.sabores?.length || t.sabores.includes(p.id))
        : p.id === t.productoId,
    );

  const proximoHorario = useMemo(() => {
    if (!cfg?.horarios) return null;
    const DIAS_NOMBRE = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];
    const now = new Date();
    const wd = (now.getDay() + 6) % 7;
    const ahoraMin = now.getHours() * 60 + now.getMinutes();
    const toMin = (t) => { const [hh, mm] = (t || "00:00").split(":").map(Number); return hh * 60 + mm; };
    const hoy = cfg.horarios[String(wd)];
    if (hoy?.activo && ahoraMin < toMin(hoy.desde)) return `hoy a las ${hoy.desde}`;
    for (let i = 1; i <= 7; i++) {
      const d = (wd + i) % 7;
      const h = cfg.horarios[String(d)];
      if (h?.activo) return `el ${DIAS_NOMBRE[d]} a las ${h.desde}`;
    }
    return null;
  }, [cfg]);

  const filtered = useMemo(() => {
    // En modo promo solo se muestran los productos que aplican a la promo o ya están en el carrito
    if (promoMode && promoProgress) {
      const enCarrito = new Set(cartItems.map((i) => i.productoId));
      return products.filter((p) => aplicaAPromo(p) || enCarrito.has(p.id));
    }
    const ordenados = [...products].sort((a, b) => (a.orden || 0) - (b.orden || 0));
    if (activeCategory === "todos") return ordenados;
    if (activeCategory === "pizzas") return ordenados.filter((p) => ["piedra", "molde", "xl"].includes(p.categoria));
    return ordenados.filter((p) => p.categoria === activeCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, activeCategory, promoMode, promoProgress, cartItems]);

  const lockTo = (cat) => {
    navigate(withParams(CAT_URL[cat] || "/"));
  };

  const browseAll = () => {
    setLockedCategory(null);
    setCategory("todos");
    setIntroStep(0);
    setTimeout(() => menuRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const restartIntro = () => {
    setLockedCategory(null);
    setIntroStep(1);
    if (path !== "/") navigate(withParams("/"));
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  return (
    <div className="grain min-h-screen relative">
      <Header />
      {cfg?.abierto === false && (
        <div data-testid="closed-banner" className="relative z-30 bg-red-600 text-white text-center py-2.5 px-4">
          <span className="font-black uppercase text-xs tracking-widest">Local cerrado ahora</span>
          {proximoHorario && <span className="text-xs ml-2 opacity-90 font-bold">· Abrimos {proximoHorario}</span>}
        </div>
      )}

      {cfg?.mensajePrincipal && introStep === 0 && (
        <div data-testid="mensaje-principal" className="relative z-30 bg-gold text-black text-center py-2 px-4">
          <span className="font-black uppercase text-xs tracking-widest">{cfg.mensajePrincipal}</span>
        </div>
      )}

      {introStep > 0 && !configLoaded && (
        <div data-testid="intro-loading" className="fixed inset-0 z-50 bg-ink flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-brand animate-spin" />
        </div>
      )}
      {introStep > 0 && configLoaded && (
        <div
          data-testid="intro-screen"
          className="fixed inset-0 z-50 bg-ink flex flex-col items-center justify-center p-4 sm:p-8"
        >
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand rounded-full blur-3xl opacity-20 pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-gold rounded-full blur-3xl opacity-10 pointer-events-none" />

          {introStep === 1 && (
            <>
              <div className="relative slide-up text-center mb-8 sm:mb-12">
                <div className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold font-bold mb-3">{cfg?.nombreLocal || ""}{cfg?.direccionLocal ? ` - ${cfg.direccionLocal}` : ""}</div>
                <h1 className="font-heading text-5xl sm:text-7xl lg:text-8xl uppercase text-white leading-[0.9]">
                  <ColoredTitle text={intro.paso1Titulo} />
                </h1>
              </div>

              <div className="relative slide-up w-full max-w-3xl grid grid-cols-2 gap-3 sm:gap-5">
                <IntroCard
                  testid="intro-choice-pizzas"
                  onClick={() => navigate(withParams("/pizzas"))}
                  img={resolveImg(intro.pizzas.imagen)}
                  icon={<Pizza className="w-5 h-5 sm:w-6 sm:h-6 text-black" strokeWidth={2.5} />}
                  iconBg="bg-brand"
                  title={intro.pizzas.titulo}
                  subtitle={intro.pizzas.subtitulo}
                  subtitleColor="text-gold"
                />
                <IntroCard
                  testid="intro-choice-empanadas"
                  onClick={() => lockTo("empanadas")}
                  img={resolveImg(intro.empanadas.imagen)}
                  icon={<EmpanadaIcon className="w-5 h-5 sm:w-6 sm:h-6 text-black" />}
                  iconBg="bg-gold"
                  title={intro.empanadas.titulo}
                  subtitle={intro.empanadas.subtitulo}
                  subtitleColor="text-brand"
                />
              </div>

              <button
                data-testid="intro-choice-todo"
                onClick={browseAll}
                className="relative mt-6 bg-panel border border-line hover:border-brand text-white font-black uppercase text-xs tracking-[0.25em] px-8 py-3 rounded-full transition-colors"
              >
                Ver todo el menú
              </button>
            </>
          )}

          {introStep === 2 && (
            <>
              <div className="relative slide-up text-center mb-8 sm:mb-12">
                <div className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-brand font-bold mb-3">Pizzas</div>
                <h1 className="font-heading text-5xl sm:text-7xl lg:text-8xl uppercase text-white leading-[0.9]">
                  <ColoredTitle text={intro.paso2Titulo} />
                </h1>
              </div>

              <div className="relative slide-up w-full max-w-3xl grid grid-cols-2 gap-3 sm:gap-5">
                <IntroCard
                  testid="intro-choice-piedra"
                  onClick={() => lockTo("piedra")}
                  img={resolveImg(intro.piedra.imagen)}
                  icon={<PizzaSliceIcon className="w-5 h-5 sm:w-6 sm:h-6 text-black" />}
                  iconBg="bg-brand"
                  title={intro.piedra.titulo}
                  subtitle={intro.piedra.subtitulo}
                  subtitleColor="text-gold"
                />
                <IntroCard
                  testid="intro-choice-molde"
                  onClick={() => lockTo("molde")}
                  img={resolveImg(intro.molde.imagen)}
                  icon={<MoldePizzaIcon className="w-5 h-5 sm:w-6 sm:h-6 text-black" />}
                  iconBg="bg-gold"
                  title={intro.molde.titulo}
                  subtitle={intro.molde.subtitulo}
                  subtitleColor="text-brand"
                />
              </div>

              <button
                data-testid="intro-back-step1"
                onClick={() => setIntroStep(1)}
                className="relative mt-6 flex items-center gap-2 text-zinc-500 hover:text-white text-xs uppercase tracking-[0.25em] font-bold transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Volver
              </button>
            </>
          )}
        </div>
      )}

      <main className="relative z-10">
        {!lockedCategory && !vistaPromos && promoRules.length > 0 && <PromoStrip rules={promoRules} products={products} />}

        <div ref={menuRef} />

        {promoMode && promoProgress ? (
          <div data-testid="promo-mode-banner" className="sticky top-16 z-40 bg-gold text-black border-b border-black/20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="w-4 h-4 shrink-0" strokeWidth={3} />
                <span className="font-black uppercase text-xs sm:text-sm tracking-widest truncate">Completando: {promoMode.nombre}</span>
              </div>
              <div data-testid="promo-mode-faltantes" className="text-[11px] sm:text-xs font-bold uppercase tracking-wider">
                Faltan: {faltantesTexto(promoProgress.targets) || "—"}
              </div>
              <button
                data-testid="promo-mode-cancel"
                onClick={() => { const s = promoMode?.slug; clearPromoMode(); if (s) navigate(withParams("/")); }}
                className="flex items-center gap-1 bg-black text-white hover:bg-line font-black uppercase text-[10px] tracking-widest px-3 py-1.5 rounded transition-colors shrink-0"
              >
                <X className="w-3 h-3" strokeWidth={3} /> Cancelar
              </button>
            </div>
          </div>
        ) : lockedCategory ? (
          <div data-testid="locked-category-header" className="sticky top-16 z-30 bg-ink/95 backdrop-blur-lg border-b border-line">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="bg-brand text-black text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded">Tu elección</span>
                <h2 className="font-heading text-2xl sm:text-3xl uppercase text-white">{LOCKED_LABELS[lockedCategory]}</h2>
              </div>
              <button
                data-testid="locked-change-choice"
                onClick={restartIntro}
                className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-zinc-400 hover:text-white transition-colors shrink-0"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Volver
              </button>
            </div>
          </div>
        ) : (
          <CategoryTabs active={category} onChange={(c) => navigate(withParams(c === "todos" ? "/" : (CAT_URL[c] || "/")))} orden={cfg?.ordenCategorias} />
        )}

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {vistaPromos && !promoMode ? (
            <div data-testid="promos-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {promoRules.map((r) => (
                <div key={r.id} data-testid={`promo-card-${slugify(r.nombre)}`} className="bg-panel border border-line rounded-xl p-5 flex flex-col">
                  <div className="flex items-center gap-1.5 text-gold text-[10px] font-black uppercase tracking-widest mb-1">
                    <Tag className="w-3 h-3" strokeWidth={3} /> Promo activa hoy
                  </div>
                  <div className="font-heading text-2xl uppercase text-white">{r.nombre}</div>
                  {r.descripcion && <p className="text-sm text-zinc-400 mt-1">{r.descripcion}</p>}
                  <div className="mt-auto pt-4 flex items-center justify-between gap-2">
                    {r.precioCombo ? (
                      <span className="font-heading text-2xl text-gold">{formatARS(r.precioCombo)}</span>
                    ) : (
                      <span className="font-heading text-2xl text-gold">-{r.descuentoPorcentaje}%</span>
                    )}
                    <button
                      data-testid={`promo-armar-${slugify(r.nombre)}`}
                      onClick={() => navigate(withParams(`/promo/${slugify(r.nombre)}`))}
                      className="bg-brand hover:bg-brand-strong text-black font-black uppercase text-[11px] tracking-widest px-4 py-2 rounded transition-colors"
                    >
                      Armar promo
                    </button>
                  </div>
                </div>
              ))}
              {promoRules.length === 0 && (
                <div className="col-span-full text-center py-20 text-zinc-500 uppercase tracking-widest text-sm">Hoy no hay promos activas</div>
              )}
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-20 text-zinc-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando menú...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-zinc-500 uppercase tracking-widest text-sm">
              No hay productos en esta categoría
            </div>
          ) : (
            <div
              data-testid="catalog-grid"
              className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6"
            >
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} promoLock={promoLockFor(p)} />
              ))}
            </div>
          )}
        </section>

        <footer className="border-t border-line py-10 mt-10">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="font-heading text-3xl uppercase text-white"><ColoredTitle text={cfg?.nombreLocal || ""} /></div>
            <p className="text-xs text-zinc-500 uppercase tracking-widest mt-2">{cfg?.direccionLocal ? `${cfg.direccionLocal} · ` : ""}Delivery · Retiro en Local</p>
          </div>
        </footer>
      </main>

      <CartDrawer combos={combos} />

      {/* Barra fija móvil: cantidad, total y acceso al pedido */}
      <MobileCartBar />
    </div>
  );
}

function MobileCartBar() {
  const { count, subtotal, setIsOpen, isOpen } = useCart();
  const location = useLocation();
  if (count === 0 || isOpen) return null;
  if (location.pathname.startsWith("/exito")) return null;
  return (
    <div data-testid="mobile-cart-bar" className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-panel border-t border-line backdrop-blur-xl px-4 py-3 flex items-center justify-between gap-3 slide-up">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{count} {count === 1 ? "producto" : "productos"}</div>
        <div data-testid="mobile-cart-total" className="font-heading text-2xl text-gold leading-none">{formatARS(subtotal)}</div>
      </div>
      <button
        data-testid="mobile-cart-ver-pedido"
        onClick={() => setIsOpen(true)}
        className="bg-brand hover:bg-brand-strong text-black font-black uppercase text-xs tracking-widest px-5 py-3 rounded-md transition-colors shrink-0"
      >
        Ver pedido
      </button>
    </div>
  );
}

function ColoredTitle({ text }) {
  const words = (text || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  const last = words[words.length - 1];
  const rest = words.slice(0, -1).join(" ");
  return (
    <>
      {rest}{rest ? " " : ""}<span className="text-brand">{last}</span>
    </>
  );
}

function EmpanadaIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M4 15a8 8 0 0 1 16 0Z" />
      <path d="M4 15c1.1 1.3 2.1 1.3 3.2 0c1.1 1.3 2.1 1.3 3.2 0c1.1 1.3 2.1 1.3 3.2 0c1.1 1.3 2.1 1.3 3.2 0c1.1 1.3 2.1 1.3 3.2 0" />
    </svg>
  );
}

function PizzaSliceIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M4 7a10 10 0 0 1 16 0" />
      <path d="M4 7l8 14l8 -14" />
      <circle cx="10.5" cy="11" r="1.1" />
      <circle cx="13.8" cy="13.8" r="1.1" />
    </svg>
  );
}

function MoldePizzaIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="10" cy="10.5" r="1" />
      <circle cx="14" cy="13.5" r="1" />
      <circle cx="12.8" cy="9.6" r="1" />
    </svg>
  );
}

function IntroCard({ testid, onClick, img, icon, iconBg, title, subtitle, subtitleColor }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="group relative rounded-2xl overflow-hidden border-2 border-line hover:border-brand transition-colors duration-300 aspect-[4/5] sm:aspect-square bg-black"
    >
      {img && (
        <img
          key={img}
          src={img}
          alt={title}
          onLoad={() => setLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-[transform,opacity] duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/10" />
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-left">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full ${iconBg} flex items-center justify-center mb-2 sm:mb-3 rotate-[-6deg] group-hover:rotate-0 transition-transform duration-300`}>
          {icon}
        </div>
        <div className="font-heading text-3xl sm:text-5xl uppercase text-white leading-none">{title}</div>
        <div className={`mt-1 flex items-center gap-1 ${subtitleColor} text-[11px] sm:text-xs uppercase tracking-widest font-bold`}>
          {subtitle} <ChevronRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </button>
  );
}
